// src/dnd/views/Sidebar_parts/MapInfoTab.js
import React from "react";

/**
 * Map Info (Player)
 * - Read-only summary of discovered POIs
 * - Visible Zones list
 * - Live map presence list
 */
export default function MapInfoTab({
  styles = {},
  TIP_BAR_HEIGHT = 68,
  pois = [],
  zones = [],
  visibleZones = new Set(),
  typeColours = {},
  isMapOwner = false,
  onAddNewRegionMap = () => {},
  mapPresenceUsers = [],
  onlineMapUserIds = new Set(),
  mapPresenceUpdatedAt = 0,
}) {
  const fontColour = styles.font_colour || "#fff";

  const grouped = pois.reduce((acc, p) => {
    const key = `${p.category || "Other"}|${p.type || "Unknown"}`;
    (acc[key] ||= []).push(p);
    return acc;
  }, {});

  const zonesVisible = zones.filter(z => visibleZones.has(z.id));

  const dedupedViewersMap = new Map();

  (Array.isArray(mapPresenceUsers) ? mapPresenceUsers : []).forEach((u) => {
    if (!u) return;

    const userId = Number(u?.id ?? u?.user_id);
    const hasValidUserId = Number.isFinite(userId) && userId > 0;
    const key = hasValidUserId ? `user:${userId}` : `name:${String(u?.username || u?.display_name || u?.name || "anonymous").trim().toLowerCase()}`;
    const existing = dedupedViewersMap.get(key);
    const currentLastSeen = Number(u?.last_seen) || 0;
    const existingLastSeen = Number(existing?.last_seen) || 0;

    if (!existing || currentLastSeen >= existingLastSeen) {
      dedupedViewersMap.set(key, {
        ...u,
        id: hasValidUserId ? userId : (u?.id ?? u?.user_id ?? null),
        user_id: hasValidUserId ? userId : (u?.user_id ?? u?.id ?? null),
        last_seen: currentLastSeen,
      });
    }
  });

  const viewers = Array.from(dedupedViewersMap.values()).sort((a, b) => {
    const aName = String(a?.username || a?.display_name || a?.name || `User ${a?.id || ""}`).toLowerCase();
    const bName = String(b?.username || b?.display_name || b?.name || `User ${b?.id || ""}`).toLowerCase();
    return aName.localeCompare(bName);
  });

  const updatedLabel = mapPresenceUpdatedAt
    ? new Date(mapPresenceUpdatedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "";

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        background: "transparent",
        padding: "10px 10px",
        paddingBottom: `${TIP_BAR_HEIGHT + 8}px`,
      }}
    >
      <h3 style={{ color: fontColour, marginTop: 4, marginBottom: 8 }}>Map Info</h3>

      <div style={{ marginBottom: 12, opacity: 0.8 }}>
        Read-only view of discovered content. Your GM controls visibility.
      </div>

      {isMapOwner && (
        <div style={{ marginBottom: 14 }}>
          <button
            onClick={onAddNewRegionMap}
            style={{
              backgroundColor: "#000",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 12px",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              boxShadow: "0 2px 8px #0008",
            }}
            title="Add a new child/region map"
          >
            + Add Region Map
          </button>
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>
          Viewing This Map{" "}
          <span style={{ opacity: 0.7, fontWeight: 400 }}>
            ({viewers.length})
          </span>
        </div>

        {viewers.length === 0 ? (
          <div style={{ opacity: 0.7 }}>No one else is currently viewing this map.</div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {viewers.map((viewer, index) => {
              const userId = Number(viewer?.id ?? viewer?.user_id);
              const label =
                viewer?.display_name ||
                viewer?.username ||
                viewer?.name ||
                `User ${viewer?.id ?? viewer?.user_id ?? "?"}`;
              const isOnline = onlineMapUserIds instanceof Set
                ? onlineMapUserIds.has(userId)
                : false;

              return (
                <li
                  key={Number.isFinite(userId) && userId > 0 ? `viewer-${userId}` : `viewer-${label}-${index}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 0",
                    borderBottom: "1px dashed #333",
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: isOnline ? "#4ade80" : "#666",
                      display: "inline-block",
                      boxShadow: isOnline ? "0 0 8px #4ade8088" : "none",
                      flex: "0 0 auto",
                    }}
                  />
                  <span style={{ fontWeight: 600 }}>{label}</span>
                </li>
              );
            })}
          </ul>
        )}

        {updatedLabel ? (
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.6 }}>
            Updated {updatedLabel}
          </div>
        ) : null}
      </div>

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Visible Zones</div>
        {zonesVisible.length === 0 ? (
          <div style={{ opacity: 0.7 }}>No zones revealed yet.</div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {zonesVisible.map(z => (
              <li key={z.id} style={{ padding: "4px 0", borderBottom: "1px dashed #333" }}>
                {z.label || `Zone ${z.id}`}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Discovered POIs</div>
        {Object.keys(grouped).length === 0 ? (
          <div style={{ opacity: 0.7 }}>No points discovered yet.</div>
        ) : (
          Object.entries(grouped).map(([key, items]) => {
            const [category, type] = key.split("|");
            const color = typeColours[type] || "#777";
            return (
              <div key={key} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
                  <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: "inline-block" }} />
                  {category} — {type} <span style={{ opacity: 0.7, fontWeight: 400 }}>({items.length})</span>
                </div>
                <ul style={{ listStyle: "none", paddingLeft: 18, margin: "4px 0 0 0" }}>
                  {items.map(p => (
                    <li key={p.id} style={{ padding: "2px 0", borderBottom: "1px dashed #2e2e2e" }}>
                      {p.poi_name || "(unnamed)"} {p.subtitle ? `— ${p.subtitle}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}